import Box from "@mui/material/Box";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { useTranslation } from "react-i18next";

import { Typography } from "@/components/Typography";
import type { InterludeTypes } from "@/interlude";
import { TypeBadge } from "@/pages/applications/pages/customFields/components/TypeBadge";

type CustomField = InterludeTypes["CustomField"];

interface FieldMetadataDisplayProps {
  field: CustomField;
  translationKeyPrefix: string;
}

const styles = {
  fieldInfo: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    padding: "12px",
    backgroundColor: theme.colors.staticBgPlain,
    borderRadius: "8px",
  },
  fieldInfoRow: {
    display: "grid",
    gridTemplateColumns: "100px 1fr",
    alignItems: "baseline",
    gap: "8px",
    minHeight: "24px",
  },
  fieldInfoValue: {
    minWidth: 0,
    wordBreak: "break-word",
    overflowWrap: "break-word",
    display: "flex",
    alignItems: "baseline",
    lineHeight: 1.5,
  },
  typeBadgeWrapper: {
    margin: 0,
    padding: 0,
    lineHeight: 0,
    "& .MuiChip-root": {
      margin: 0,
      verticalAlign: "baseline",
    },
    "& .MuiChip-root .MuiChip-label": {
      paddingLeft: 0,
    },
  },
  keyBadge: {
    fontSize: "13px",
    color: theme.colors.staticFgNote,
    backgroundColor: theme.colors.staticBgPlain,
    padding: "4px 8px 4px 0",
    borderRadius: "4px",
    display: "inline-block",
    wordBreak: "break-word",
    overflowWrap: "break-word",
    lineHeight: 1.5,
    verticalAlign: "baseline",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

export const FieldMetadataDisplay: React.FC<FieldMetadataDisplayProps> =
  function FieldMetadataDisplay({ field, translationKeyPrefix }) {
    const { t } = useTranslation();

    return (
      <Box sx={styles.fieldInfo}>
        <Box sx={styles.fieldInfoRow}>
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t(`${translationKeyPrefix}.fieldName`)}
          </Typography>
          <Box sx={styles.fieldInfoValue}>
            <Typography
              variant="body"
              fontWeight={500}
              color={theme.colors.staticFgTitle}
            >
              {field.name}
            </Typography>
          </Box>
        </Box>
        <Box sx={styles.fieldInfoRow}>
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t(`${translationKeyPrefix}.fieldKey`)}
          </Typography>
          <Box sx={styles.fieldInfoValue}>
            <Box component="span" sx={styles.keyBadge}>
              {field.key}
            </Box>
          </Box>
        </Box>
        <Box sx={styles.fieldInfoRow}>
          <Typography variant="body" color={theme.colors.staticFgNote}>
            {t(`${translationKeyPrefix}.fieldType`)}
          </Typography>
          <Box sx={styles.fieldInfoValue}>
            <Box component="span" sx={styles.typeBadgeWrapper}>
              <TypeBadge fieldType={field.type} />
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };
